// This package is the sanctioned wrapper around @tanstack/react-virtual, so it is the one place
// allowed to import it directly (consumers are steered here by the no-restricted-imports rule).
// eslint-disable-next-line no-restricted-imports
import {
  defaultRangeExtractor as tanstackDefaultRangeExtractor,
  type PartialKeys,
  type Range as TanstackRange,
  type ReactVirtualizer,
  type ReactVirtualizerOptions,
  type ScrollToOptions as TanstackScrollToOptions,
  useVirtualizer as useTanstackVirtualizer,
  useWindowVirtualizer as useTanstackWindowVirtualizer,
  type VirtualItem as TanstackVirtualItem,
} from '@tanstack/react-virtual'

// Re-exported as type aliases (not `export ... from`) so consumers never import
// from `@tanstack/react-virtual` directly, without tripping the no-barrel-files rule.
export type VirtualItem = TanstackVirtualItem
export type Range = TanstackRange
export type ScrollToOptions = TanstackScrollToOptions

/** Re-export of tanstack's `defaultRangeExtractor` so consumers never import it directly. */
export const defaultRangeExtractor = tanstackDefaultRangeExtractor

/**
 * The raw virtualizer instance type, re-exported for the rare consumer that needs
 * the escape hatch (see {@link VirtualizerView.virtualizer}).
 */
export type Virtualizer<TScrollElement extends Element | Window, TItemElement extends Element> = ReactVirtualizer<
  TScrollElement,
  TItemElement
>

/**
 * The plain, memoization-safe surface of a virtualizer. Its data fields and
 * stable callbacks are safe to read during render, because the parts that are
 * NOT memoizable — reading raw virtualizer state such as `getVirtualItems()` /
 * `getTotalSize()` during render, which makes the React Compiler bail out — stay
 * contained inside this package. A raw instance is still reachable through the
 * `virtualizer` escape hatch for imperative use, so consuming render paths remain
 * fully compilable only as long as they read these plain fields and avoid the
 * escape hatch during render.
 */
export interface VirtualizerView<TScrollElement extends Element | Window, TItemElement extends Element> {
  /** Rows currently inside the virtual window (plain data, incl. `start`). */
  items: VirtualItem[]
  /**
   * Total main-axis size of all rows. Use it for the sizing container's height
   * unless you opt into `directDomUpdates` (which writes the size for you).
   */
  totalSize: number
  /** `scrollMargin` echoed back for sticky-header / offset math. */
  scrollMargin: number
  /** Size of the scroll viewport, or `null` before first measure. */
  scrollRect: {width: number; height: number} | null
  /**
   * Ref for the inner sizing container. Only meaningful with `directDomUpdates`,
   * where the virtualizer writes the container's main-axis size directly to the
   * DOM (so the container must NOT set `height` / `width` in its style).
   */
  containerRef: ReactVirtualizer<TScrollElement, TItemElement>['containerRef']
  /**
   * Ref for each row element. Registers the element for measurement and, when
   * `directDomUpdates` is enabled, for direct DOM positioning.
   */
  measureElement: ReactVirtualizer<TScrollElement, TItemElement>['measureElement']
  scrollToIndex: ReactVirtualizer<TScrollElement, TItemElement>['scrollToIndex']
  scrollToOffset: ReactVirtualizer<TScrollElement, TItemElement>['scrollToOffset']
  /** Imperatively re-measure all measured elements (rarely needed). */
  measure: ReactVirtualizer<TScrollElement, TItemElement>['measure']
  /**
   * ESCAPE HATCH — the raw virtualizer instance. Use ONLY inside effects and
   * event handlers (imperative scroll, live `scrollOffset` reads that must not
   * trigger a re-render). NEVER read its state during render (`getVirtualItems()`,
   * `getTotalSize()`, …) — that reintroduces the compiler bail-out this package
   * exists to contain. Prefer the plain fields above wherever possible.
   */
  virtualizer: Virtualizer<TScrollElement, TItemElement>
}

function toView<TScrollElement extends Element | Window, TItemElement extends Element>(
  virtualizer: ReactVirtualizer<TScrollElement, TItemElement>,
): VirtualizerView<TScrollElement, TItemElement> {
  return {
    items: virtualizer.getVirtualItems(),
    totalSize: virtualizer.getTotalSize(),
    scrollMargin: virtualizer.options.scrollMargin,
    scrollRect: virtualizer.scrollRect,
    containerRef: virtualizer.containerRef,
    measureElement: virtualizer.measureElement,
    scrollToIndex: virtualizer.scrollToIndex,
    scrollToOffset: virtualizer.scrollToOffset,
    measure: virtualizer.measure,
    virtualizer,
  }
}

// Options mirror `@tanstack/react-virtual` exactly. `directDomUpdates` is NOT
// forced on — it stays an opt-in option (alongside `directDomUpdatesMode`) that a
// consumer passes once a list is ready for direct DOM positioning. This package's
// job is the compiler-safe `VirtualizerView`, not enabling directDomUpdates.
type ElementVirtualizerOptions<TScrollElement extends Element, TItemElement extends Element> = PartialKeys<
  ReactVirtualizerOptions<TScrollElement, TItemElement>,
  'observeElementRect' | 'observeElementOffset' | 'scrollToFn'
>

type WindowVirtualizerOptions<TItemElement extends Element> = PartialKeys<
  ReactVirtualizerOptions<Window, TItemElement>,
  'getScrollElement' | 'observeElementRect' | 'observeElementOffset' | 'scrollToFn'
>

/**
 * Compiler-safe wrapper around `@tanstack/react-virtual`'s `useVirtualizer`.
 * Returns a {@link VirtualizerView} of plain values and stable callbacks instead
 * of the mutable virtualizer instance, so the React Compiler bail-out stays
 * contained here.
 */
export function useVirtualizer<TScrollElement extends Element, TItemElement extends Element>(
  options: ElementVirtualizerOptions<TScrollElement, TItemElement>,
): VirtualizerView<TScrollElement, TItemElement> {
  'use no memo' // react virtualizer is unsupported, so we pull the behavior here to avoid the compiler bail-out. This is a temporary workaround until support exists in the virtualization library.
  const virtualizer = useTanstackVirtualizer<TScrollElement, TItemElement>(options)
  return toView(virtualizer)
}

/**
 * Compiler-safe wrapper around `@tanstack/react-virtual`'s `useWindowVirtualizer`.
 * Returns a {@link VirtualizerView} of plain values and stable callbacks instead
 * of the mutable virtualizer instance, so the React Compiler bail-out stays
 * contained here.
 */
export function useWindowVirtualizer<TItemElement extends Element>(
  options: WindowVirtualizerOptions<TItemElement>,
): VirtualizerView<Window, TItemElement> {
  'use no memo' // react virtualizer is unsupported, so we pull the behavior here to avoid the compiler bail-out. This is a temporary workaround until support exists in the virtualization library.
  const virtualizer = useTanstackWindowVirtualizer<TItemElement>(options)
  return toView(virtualizer)
}
